import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import copy from 'copy-to-clipboard'
import { StringParam, useQueryParam } from 'use-query-params'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  ModalVariables,
  Select,
  Table,
  TableSkeleton,
  TopProgress,
  Typography,
} from '../../../components'
import { staffObjectStorageService } from '../../../api'
import { BucketFile, BucketFolder } from '../../../api/staffObjectStorageService'
import { useUi } from '../../../context/ui.context'
import { icons } from '../../../icons'
import { formatFileSize } from '../../../utils/sizes'
import {
  Card,
  CardTitle,
  ClickableName,
  Content,
  EmptyText,
  FormActions,
  JsonPreview,
  PathPart,
  PathTrail,
  Toolbar,
  ToolbarGroup,
} from './styles'
import { getObjectStorageError } from './utils'

type Props = { bucketId: string }
type Entry = {
  id: string
  kind: 'folder' | 'file'
  name: string
  path: string
  [key: string]: any
}

const getFolderPath = (folder: BucketFolder) => String(folder.Prefix || folder.prefix || folder.Key || '')
const getFilePath = (file: BucketFile) => String(file.Key || file.key || file.name || '')

const BucketFiles: FC<Props> = ({ bucketId }) => {
  const [t] = useTranslation()
  const { viewAlert, updateAlertProgress } = useUi()
  const [queryPath, setQueryPath] = useQueryParam('path', StringParam)
  const path = queryPath || ''
  const uploadRef = useRef<HTMLInputElement>(null)
  const [folders, setFolders] = useState<BucketFolder[]>([])
  const [files, setFiles] = useState<BucketFile[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [pageLoading, setPageLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [modalType, setModalType] = useState<any>('')
  const [selectedEntry, setSelectedEntry] = useState<Entry | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [fileDetails, setFileDetails] = useState<Record<string, unknown> | null>(null)
  const [visibilityOpen, setVisibilityOpen] = useState(false)
  const [fileAccess, setFileAccess] = useState('')

  const loadPath = (nextCursor?: string, append = false) => {
    setLoading(true)
    staffObjectStorageService.getPath(bucketId, path, nextCursor)
      .then(({ data }) => {
        setFolders((current) => append ? [...current, ...data.folders] : data.folders)
        setFiles((current) => append ? [...current, ...data.files] : data.files)
        setCursor(data.cursor)
      })
      .catch((error) => viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      }))
      .finally(() => {
        setPageLoading(false)
        setLoading(false)
      })
  }

  useEffect(() => {
    setPageLoading(true)
    loadPath()
  }, [bucketId, path])

  const openModal = (type: string, entry?: Entry) => {
    setSelectedEntry(entry || null)
    setModalType(type)
    setViewModal(true)
  }

  const closeModal = async (values?: any) => {
    try {
      setLoading(true)
      if (modalType === 'add-folder') {
        await staffObjectStorageService.createFolder(bucketId, `${path}${values.name}`)
      } else if (modalType === 'delete' && selectedEntry) {
        if (selectedEntry.kind === 'folder') {
          await staffObjectStorageService.deleteFolder(bucketId, selectedEntry.path)
        } else {
          await staffObjectStorageService.deleteFile(bucketId, selectedEntry.path)
        }
      } else if (selectedEntry) {
        await staffObjectStorageService.updateFile(bucketId, selectedEntry.path, values)
      }
      setViewModal(false)
      setSelectedEntry(null)
      viewAlert({ severity: 'information', message: t('CHANGES_SAVED') })
      loadPath()
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setLoading(false)
    }
  }

  const showDetails = async (entry: Entry) => {
    try {
      setLoading(true)
      const { data } = await staffObjectStorageService.getFile(bucketId, entry.path)
      setSelectedEntry(entry)
      setFileDetails(data)
      setDetailsOpen(true)
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setLoading(false)
    }
  }

  const openVisibility = async (entry: Entry) => {
    try {
      setLoading(true)
      setSelectedEntry(entry)
      setFileAccess('')
      const { data } = await staffObjectStorageService.getFile(bucketId, entry.path)
      const acl = data.ACL as any
      const currentAccess = typeof acl === 'string' ? acl : acl?.access || acl?.Access
      if (typeof currentAccess === 'string') setFileAccess(currentAccess)
      setVisibilityOpen(true)
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setLoading(false)
    }
  }

  const saveVisibility = async () => {
    if (!selectedEntry || !fileAccess) return
    try {
      setLoading(true)
      await staffObjectStorageService.updateFile(bucketId, selectedEntry.path, { access: fileAccess })
      setVisibilityOpen(false)
      viewAlert({ severity: 'information', message: t('CHANGES_SAVED') })
      loadPath()
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setLoading(false)
    }
  }

  const copyPublicLink = async (entry: Entry) => {
    try {
      setLoading(true)
      const { data } = await staffObjectStorageService.getFile(bucketId, entry.path, true)
      const link = data.Link as any
      const url = typeof link === 'string' ? link : link?.url || link?.URL || link?.href
      if (!url) throw new Error(`${t('PUBLIC_LINK_NOT_RETURNED')}`)
      copy(url)
      viewAlert({ severity: 'information', message: t('COPIED') })
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: error instanceof Error && !error?.message?.includes('Request')
          ? error.message
          : getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setLoading(false)
    }
  }

  const uploadFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    formData.append('name', file.name)
    if (path) formData.append('path', path.replace(/\/$/, ''))

    const alertId = viewAlert({ severity: 'file', message: file.name, canClose: true })
    try {
      setUploading(true)
      await staffObjectStorageService.uploadFile(
        bucketId,
        formData,
        (progress) => updateAlertProgress(alertId, progress),
      )
      viewAlert({ severity: 'information', message: t('SUCCESSFULY_UPLOAD_FILE') })
      loadPath()
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setUploading(false)
      event.target.value = ''
    }
  }

  const folderRows: Entry[] = folders.map((folder, index) => {
    const folderPath = getFolderPath(folder)
    const name = folderPath.replace(path, '').replace(/\/$/, '') || folderPath
    const entry: Entry = {
      id: `folder-${folderPath || index}`,
      kind: 'folder',
      path: folderPath,
      name,
      display_name: (
        <ClickableName onClick={() => setQueryPath(folderPath)}>
          <strong>{name}</strong>
        </ClickableName>
      ),
      size: '-',
      last_modified: '-',
      storage_class: t('FOLDER'),
    }
    entry.dropOptions = [{
      id: 'delete-folder',
      title: t('DELETE'),
      icon: icons.DeleteIcon,
      divider: false,
      onClick: () => openModal('delete', entry),
    }]
    return entry
  })

  const fileRows: Entry[] = files.map((file, index) => {
    const filePath = getFilePath(file)
    const name = filePath.replace(path, '') || filePath
    const entry: Entry = {
      id: `file-${filePath || index}`,
      kind: 'file',
      path: filePath,
      name,
      display_name: name,
      size: file.Size === undefined ? 'N/A' : formatFileSize(file.Size),
      last_modified: file.LastModified ? moment(file.LastModified).format('DD.MM.YYYY HH:mm') : 'N/A',
      storage_class: file.StorageClass || 'N/A',
    }
    entry.dropOptions = [
      { id: 'info', title: t('MORE_INFO'), icon: icons.Info, divider: false, onClick: () => showDetails(entry) },
      { id: 'visibility', title: t('VISIBILITY'), icon: icons.EyeIc, divider: false, onClick: () => openVisibility(entry) },
      { id: 'metadata', title: t('OBJECT_METADATA'), icon: icons.DocumentCodeIc, divider: false, onClick: () => openModal('bucket-metadata', entry) },
      { id: 'tags', title: t('OBJECT_TAGS'), icon: icons.CodeCircleIc, divider: false, onClick: () => openModal('bucket-file-tags', entry) },
      { id: 'rename', title: t('EDIT_NAME'), icon: icons.Edit, divider: false, onClick: () => openModal('rename', entry) },
      { id: 'link', title: t('GET_PUBLIC_LINK'), icon: icons.ExportIc, divider: false, onClick: () => copyPublicLink(entry) },
      { id: 'delete', title: t('DELETE'), icon: icons.DeleteIcon, divider: false, onClick: () => openModal('delete', entry) },
    ]
    return entry
  })

  const rows = [...folderRows, ...fileRows]
  const columns = [
    { title: t('NAME'), key: 'display_name' },
    { title: t('SIZE'), key: 'size' },
    { title: t('LAST_MODIFIED'), key: 'last_modified' },
    { title: t('STORAGE_CLASS'), key: 'storage_class' },
    { title: '', key: 'props' },
  ]
  const pathParts = path.split('/').filter(Boolean)

  if (pageLoading) {
    return (
      <Content>
        <TopProgress loading />
        <TableSkeleton rows={7} columns={columns.length} columnsData={columns} />
      </Content>
    )
  }

  return (
    <Content>
      <Toolbar>
        <PathTrail>
          <PathPart onClick={() => setQueryPath(undefined)}>{t('ROOT')}</PathPart>
          {pathParts.map((part, index) => {
            const target = `${pathParts.slice(0, index + 1).join('/')}/`
            return (
              <React.Fragment key={target}>
                <Typography variant="body">/</Typography>
                <PathPart onClick={() => setQueryPath(target)}>{part}</PathPart>
              </React.Fragment>
            )
          })}
        </PathTrail>
        <ToolbarGroup>
          <Button variant="stroke" size="display" title={`+ ${t('ADD_FOLDER')}`} onClick={() => openModal('add-folder')} />
          <input ref={uploadRef} hidden type="file" onChange={uploadFile} />
          <Button
            variant="primary"
            size="display"
            title={t('UPLOAD')}
            loading={uploading}
            onClick={() => uploadRef.current?.click()}
          />
        </ToolbarGroup>
      </Toolbar>
      {rows.length ? (
        <Table
          columns={columns}
          rows={rows}
          hideOther
          hideCheckbox
          loading={loading}
        />
      ) : (
        <EmptyText>{t('NO_FILES_YET')}</EmptyText>
      )}
      {cursor ? (
        <FormActions>
          <Button
            variant="stroke"
            size="display"
            title={t('LOAD_MORE')}
            loading={loading}
            onClick={() => loadPath(cursor, true)}
          />
        </FormActions>
      ) : null}
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={viewModal}
        closeModal={closeModal}
        closeModalCancel={() => setViewModal(false)}
        alertTitle={modalType === 'add-folder' ? t('ADD_FOLDER') : selectedEntry?.name || ''}
        alertSubTitle={selectedEntry ? [selectedEntry] : []}
        instance={selectedEntry ? {
          ...selectedEntry,
          clear_name: selectedEntry.name,
          bucketId,
          name: selectedEntry.path,
          isFileNameBucket: selectedEntry.kind === 'file',
        } : undefined}
        loading={loading}
      />
      <Dialog noIframe open={detailsOpen} active={detailsOpen} handleClose={() => setDetailsOpen(false)}>
        <Card style={{ width: 'min(620px, calc(100vw - 32px))' }}>
          <CardTitle>{selectedEntry?.name}</CardTitle>
          <JsonPreview>{JSON.stringify(fileDetails, null, 2)}</JsonPreview>
          <Button variant="stroke" size="display" title={t('CLOSE')} onClick={() => setDetailsOpen(false)} />
        </Card>
      </Dialog>
      <Dialog noIframe open={visibilityOpen} active={visibilityOpen} handleClose={() => setVisibilityOpen(false)}>
        <Card style={{ width: 'min(480px, calc(100vw - 32px))' }}>
          <CardTitle>{t('OBJECT_VISIBILITY')}</CardTitle>
          <Typography variant="body">{selectedEntry?.name}</Typography>
          <Select
            title={t('VISIBILITY')}
            placeholder={t('CHOOSE')}
            data={[
              { value: 'private', label: t('PRIVATE') },
              { value: 'public-read', label: 'public-read' },
              { value: 'public-read-write', label: 'public-read-write' },
              { value: 'authenticated-read', label: 'authenticated-read' },
            ]}
            selectedValue={fileAccess}
            onChange={setFileAccess}
          />
          <FormActions>
            <Button variant="stroke" size="display" title={t('CANCEL')} onClick={() => setVisibilityOpen(false)} />
            <Button
              type="button"
              variant="primary"
              size="display"
              title={t('SAVE')}
              loading={loading}
              disabled={!fileAccess}
              onClick={saveVisibility}
            />
          </FormActions>
        </Card>
      </Dialog>
    </Content>
  )
}

export default BucketFiles
